import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Loader2, Save } from "lucide-react";

/**
 * FulfillmentSettingsCard — pickup config + tipping toggle for the
 * dashboard. Simple 7-day open/close grid (per-day enable + hh:mm)
 * plus a lead-time minutes input. The checkout page generates 15-min
 * slots from these windows on the fly, so no per-order scheduling
 * backend is needed for the first cut.
 */
const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"],
  ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];

const DEFAULT_WINDOW = { open: "09:00", close: "18:00", enabled: true };

function normalizeWindows(input) {
  const out = {};
  DAYS.forEach(([k]) => {
    const w = input && input[k] ? input[k] : null;
    out[k] = {
      open: w?.open || DEFAULT_WINDOW.open,
      close: w?.close || DEFAULT_WINDOW.close,
      enabled: w?.enabled !== false,
    };
  });
  return out;
}

export default function FulfillmentSettingsCard({ business, onSaved }) {
  const initial = business?.fulfillment || {};
  const [pickupEnabled, setPickupEnabled] = useState(initial.pickup_enabled !== false);
  const [leadMinutes, setLeadMinutes] = useState(Number(initial.pickup_lead_minutes || 30));
  const [windows, setWindows] = useState(() => normalizeWindows(initial.pickup_windows));
  const [address, setAddress] = useState(initial.pickup_address || "");
  const [tipping, setTipping] = useState(!!initial.tipping_enabled);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const f = business?.fulfillment || {};
    setPickupEnabled(f.pickup_enabled !== false);
    setLeadMinutes(Number(f.pickup_lead_minutes || 30));
    setWindows(normalizeWindows(f.pickup_windows));
    setAddress(f.pickup_address || "");
    setTipping(!!f.tipping_enabled);
  }, [business?.business_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchWindow = (day, patch) =>
    setWindows((w) => ({ ...w, [day]: { ...w[day], ...patch } }));

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(
        `${API}/businesses/${business.business_id}/fulfillment`,
        {
          pickup_enabled: pickupEnabled,
          delivery_enabled: false,
          pickup_lead_minutes: Math.max(0, Number(leadMinutes) || 0),
          pickup_windows: windows,
          pickup_address: address.trim() || null,
          tipping_enabled: tipping,
        },
        { withCredentials: true },
      );
      toast.success("Fulfillment settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 space-y-5" data-testid="fulfillment-settings-card">
      <div>
        <h3 className="font-heading text-lg text-tbc-navy">Pickup & tipping</h3>
        <p className="text-xs text-gray-500 font-body mt-0.5">
          Controls what buyers see on the storefront checkout page.
        </p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={pickupEnabled}
          onChange={(e) => setPickupEnabled(e.target.checked)}
          className="mt-1 w-4 h-4 accent-tbc-gold"
          data-testid="fulfillment-pickup-enabled"
        />
        <div>
          <p className="font-body text-sm text-tbc-navy">Offer pickup</p>
          <p className="text-xs text-gray-500 font-body">
            Buyers pick a time slot at checkout and collect from your address.
          </p>
        </div>
      </label>

      {pickupEnabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-body text-tbc-navy/70">Lead time (minutes)</label>
              <input
                type="number"
                min={0}
                step={15}
                value={leadMinutes}
                onChange={(e) => setLeadMinutes(Number(e.target.value) || 0)}
                className="mt-1 w-full h-10 rounded-lg border border-gray-200 px-3 text-sm font-body"
                data-testid="fulfillment-lead-minutes"
              />
              <p className="text-[11px] text-gray-500 mt-0.5">Earliest slot = now + lead.</p>
            </div>
            <div>
              <label className="text-xs font-body text-tbc-navy/70">Pickup address (optional)</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={business?.address || "123 Main St, City, ST"}
                className="mt-1 w-full h-10 rounded-lg border border-gray-200 px-3 text-sm font-body"
                data-testid="fulfillment-address"
              />
              <p className="text-[11px] text-gray-500 mt-0.5">Falls back to your business address.</p>
            </div>
          </div>

          <div>
            <p className="text-xs font-body text-tbc-navy/70 mb-2">Weekly pickup windows</p>
            <div className="space-y-2">
              {DAYS.map(([k, label]) => (
                <div key={k} className="flex items-center gap-2 sm:gap-3">
                  <label className="flex items-center gap-2 min-w-[8.5rem] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!windows[k].enabled}
                      onChange={(e) => patchWindow(k, { enabled: e.target.checked })}
                      className="w-4 h-4 accent-tbc-gold"
                      data-testid={`fulfillment-day-${k}`}
                    />
                    <span className="text-sm font-body text-tbc-navy">{label}</span>
                  </label>
                  <input
                    type="time"
                    value={windows[k].open}
                    onChange={(e) => patchWindow(k, { open: e.target.value })}
                    disabled={!windows[k].enabled}
                    className="h-9 rounded-lg border border-gray-200 px-2 text-sm font-body disabled:opacity-50"
                    data-testid={`fulfillment-open-${k}`}
                  />
                  <span className="text-gray-400 text-sm">–</span>
                  <input
                    type="time"
                    value={windows[k].close}
                    onChange={(e) => patchWindow(k, { close: e.target.value })}
                    disabled={!windows[k].enabled}
                    className="h-9 rounded-lg border border-gray-200 px-2 text-sm font-body disabled:opacity-50"
                    data-testid={`fulfillment-close-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={tipping}
          onChange={(e) => setTipping(e.target.checked)}
          className="mt-1 w-4 h-4 accent-tbc-gold"
          data-testid="fulfillment-tipping-enabled"
        />
        <div>
          <p className="font-body text-sm text-tbc-navy">Enable tipping</p>
          <p className="text-xs text-gray-500 font-body">
            Buyers can add 10 / 15 / 20 % or a custom tip at checkout.
          </p>
        </div>
      </label>

      <div className="pt-2 border-t border-gray-100 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white px-5 py-2 text-sm font-semibold"
          data-testid="fulfillment-save"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>
    </div>
  );
}
